import { useModalStore } from '@/shared/lib';
import { InternalTransferModal } from '../ui';
import { ResultModal } from '../ui/ResultModal';
import { InternalTransferConfirmationModal } from '../ui/InternalTransferConfirmationModal';
import type { InternalTransferConfirmationArgs } from '.';
import type { AccountType } from '@/entities/account';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '@/entities/session';
import { useVerificationInfoModal } from '@/entities/verification';

type InternalTransferInitialContext = {
  accountType: AccountType;
  accountNumber: string;
  assetCode: string;
  direction?: 'from' | 'to';
};

type InternalTransferModalProps = {
  onClose?: () => void;
  initialContext?: InternalTransferInitialContext;
};

const useInternalTransferModals = () => {
  const { t } = useTranslation();
  const { session } = useSessionStore();
  const { showDialogComponent, closeDialog, cleanup } = useModalStore();

  const { openVerificationInfoModal } = useVerificationInfoModal();

  const isVerified = session?.additional?.isVerified || false;

  const onCloseDialog = () => {
    cleanup();
    closeDialog();
  };

  const closeConfirmation = {
    title: t('withdrawMethods.crypto.exitConfirmTitle'),
    description: t('withdrawMethods.crypto.exitConfirmDescription'),
    confirmText: t('withdrawMethods.crypto.exitConfirmButton'),
    showCancelButton: false,
  };

  const openResultModal = ({
    success,
    error,
  }: {
    success: boolean;
    error?: Error;
  }) => {
    showDialogComponent({
      component: (
        <ResultModal
          success={success}
          error={error}
          onClose={() => {
            onCloseDialog();
          }}
        />
      ),
      onClose: onCloseDialog,
    });
  };

  const openInternalTransferFormModal: (
    props?: InternalTransferModalProps
  ) => void = props => {
    if (!isVerified) {
      openVerificationInfoModal();
      return;
    }

    showDialogComponent({
      component: (
        <InternalTransferModal
          onClose={onCloseDialog}
          initialContext={props?.initialContext}
        />
      ),
      onClose: onCloseDialog,
      skipBackButtonConfirmation: false,
    });
  };

  const openConfirmationModal = (params: InternalTransferConfirmationArgs) => {
    showDialogComponent({
      component: (
        <InternalTransferConfirmationModal
          {...params}
          openResultModal={openResultModal}
          onBack={openInternalTransferFormModal}
        />
      ),
      onClose: closeDialog,
      closeConfirmation,
      skipBackButtonConfirmation: true,
      closeConfirmationRequired: true,
    });
  };

  return {
    openConfirmationModal,
    openInternalTransferFormModal,
    openResultModal,
  };
};
export { useInternalTransferModals };
